import type { FarmForecastAlertRecord } from './farmMobileAlerts';

export type AlertInfoContent = {
  title: string;
  description: string;
  legend?: string;
};

export function getSatelliteAlertInfo(kind: 'ndvi' | 'ndmi'): AlertInfoContent {
  if (kind === 'ndmi') {
    return {
      title: 'Moisture alert',
      description:
        'Moisture alerts use satellite NDMI (Normalized Difference Moisture Index) to compare recent canopy and soil moisture against your field baseline. A drop may indicate dry soil, heat stress, or delayed irrigation.',
      legend:
        'Needs attention — moisture is below typical levels for this field. Improving — moisture is recovering. Update — general status change without an urgent flag.',
    };
  }
  return {
    title: 'Vegetation alert',
    description:
      'Vegetation alerts use satellite NDVI (Normalized Difference Vegetation Index) to detect changes in crop greenness compared with your field history. Lower values may signal stress, poor establishment, pest damage, or cloud-affected imagery.',
    legend:
      'Needs attention — vegetation is below typical levels. Improving — greenness is recovering. Update — general status change without an urgent flag.',
  };
}

export function getForecastAlertInfo(alert: FarmForecastAlertRecord): AlertInfoContent {
  const cat = (alert.alert_category || '').toLowerCase();
  const type = (alert.alert_type || '').toLowerCase();

  if (type === 'forecast_trend') {
    return {
      title: 'Forecast trend',
      description:
        'Summarises how vegetation or moisture is expected to change over the coming days based on modelled satellite indices and weather inputs.',
    };
  }
  if (cat === 'ndvi_forecast') {
    return {
      title: 'Vegetation forecast',
      description:
        'Predicts near-term NDVI for your field using weather and historical vegetation patterns. Helps you plan scouting, irrigation, or nutrient applications before stress becomes visible in the field.',
    };
  }
  if (cat === 'ndmi_forecast') {
    return {
      title: 'Moisture forecast',
      description:
        'Predicts near-term canopy and soil moisture (NDMI) for your field. Useful for irrigation scheduling and early drought monitoring.',
    };
  }
  if (type === 'weather_rain') {
    return {
      title: 'Rain outlook',
      description:
        'Shows how much rain is expected at the field. A light shower is a reason to wait before spraying. Heavier rain can also delay field work because it washes chemicals off the leaves.',
    };
  }
  if (type === 'weather_heat') {
    return {
      title: 'Heat outlook',
      description:
        'Flags when maximum temperatures are expected to exceed levels that can stress rice and other crops, increasing evapotranspiration and heat-stress risk.',
    };
  }
  if (type === 'weather_drought') {
    return {
      title: 'Dry spell forecast',
      description:
        'Indicates a period of below-normal rainfall is expected, which may reduce soil moisture and increase irrigation demand.',
    };
  }
  return {
    title: 'Weather forecast alert',
    description:
      'Weather-based forecast alert for your field. Check the expected date and severity to plan field work and farmer advisories.',
  };
}

export function getCadiAlertInfo(): AlertInfoContent {
  return {
    title: 'Drought outlook (CADI)',
    description:
      'Farm-level drought outlook uses the Combined Agricultural Drought Index (CADI) sampled inside your field boundary. CADI blends land surface temperature, vegetation condition, runoff, rainfall balance, soil moisture, and evaporative stress into one 0–1 index.',
    legend:
      'Values 0.00–0.40 mean no drought, 0.40–0.55 dry, 0.55–0.65 moderate, 0.65–0.75 severe, 0.75–0.85 extreme, and 0.85–1.00 exceptional agricultural drought. The chart shows how conditions may change month by month across the forecast period.',
  };
}

export function getPestAlertInfo(): AlertInfoContent {
  return {
    title: 'Pest risk outlook',
    description:
      'Farm-level pest risk uses the Pest Occurrence Probability Index sampled inside your field boundary. Maps are seasonal climate suitability models (typical occurrence for this time of year).',
    legend:
      'Values near 0 mean very low occurrence probability; values near 1 mean very high probability of pest or disease occurrence. Alerts focus on high-impact rice pests: rice blast, bacterial leaf blight, brown planthopper, and stem borer.',
  };
}
